import React, { useEffect, useMemo, useState } from "react";
import PbMapsProps from "./PbMapProps.type";
import {
  GoogleMap,
  useJsApiLoader,
  OverlayView,
  Polyline,
} from "@react-google-maps/api";
import { CircularProgress } from "@mui/material";
import styled from "@emotion/styled";
import PbErrorMessage from "../../simple/PbErrorMessage/PbErrorMessage";
import { Easing, Tween, update } from "@tweenjs/tween.js";

const CenterContainer = styled.div({
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  width: "100%",
  height: "100%",
});

export default function PbMap<T>(props: PbMapsProps<T>) {
  const [overlays, setOverlays] = useState<google.maps.OverlayView[]>([]);
  const [polylines, setPolines] = useState<google.maps.Polyline[]>([]);
  const overlayViewRef = React.useRef([]);
  const mapRef = React.useRef();

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: props.ApiKey,
    ...props.loadOptions,
  });

  const [gMap, setGMap] = useState<google.maps.Map>();
  const [loadedMarkes, setLoadedMarkes] = useState(props.markers);
  const [loadedLines, setLoadedLines] = useState(props.polylines);
  const [currentPosition, setCurrentPosition] = useState({ lat: 0, lng: 0 });

  useEffect(() => {
    if (props.getMapRef) {
      props.getMapRef(mapRef);
    }
  }, [mapRef]);

  useEffect(() => {
    console.log(props.selectedMarker)
    if (
      props.selectedMarker &&
      props.selectedMarker.position &&
      !props.disableSelection
    ) {
      console.log("moving to marker")
      moveCameraSmooth(props.selectedMarker.position);
    }
  }, [props.selectedMarker]);

  const onLoad = React.useCallback(function callback(map: google.maps.Map) {
    const bounds = new window.google.maps.LatLngBounds(props.center);
    map.fitBounds(bounds);
    setLoadedMarkes(props.markers);
    setLoadedLines(props.polylines);
    setCurrentPosition(props.center);
    const options: google.maps.MapOptions = {
      clickableIcons: false,
      streetViewControl: false,
      disableDefaultUI: true,
      fullscreenControl: false,
    };
    map.setOptions(options);
    map.setZoom(15);
    if (props.getMapInstance) {
      props.getMapInstance(map);
    }
    setGMap(map);
  }, []);

  const onUnmount = React.useCallback(function callback(map) {
    setGMap(null);
  }, []);

  const moveCameraSmooth = (position: { lat: number; lng: number }) => {
    if (gMap) {
      const cameraOptions: google.maps.CameraOptions = {
        zoom: gMap.getZoom(),
        center: {
          lat: gMap.getCenter().lat(),
          lng: gMap.getCenter().lng(),
        },
      };
      new Tween(cameraOptions) // Create a new tween that modifies 'cameraOptions'.
        .to({ zoom: 18, center: position }, 2250) // Move to destination in 1.5 second.
        .easing(Easing.Quadratic.Out) // Use an easing function to make the animation smooth.
        .onUpdate(() => {
          gMap.moveCamera(cameraOptions);
        })
        .start() // Start the tween immediately.
        .onComplete(() => {
          if (props.onCamaraMoveEnd) {
            props.onCamaraMoveEnd();
          }
        });
      // Setup the animation loop.
      function animate(time: number) {
        requestAnimationFrame(animate);
        update(time);
      }
      requestAnimationFrame(animate);
    }
  };

  useEffect(() => {
    setLoadedMarkes(props.markers);
  }, [props.markers]);

  const getLineOptions = (index: number) => {
    const { strokeColor, strokeOpacity, strokeWeight, fillColor, fillOpacity } =
      props.lineOptions || {};
    return {
      strokeColor: strokeColor ? strokeColor : "#000000",
      strokeOpacity: strokeOpacity ? strokeOpacity : 0.8,
      strokeWeight: strokeWeight ? strokeWeight : 2,
      fillColor: fillColor ? fillColor : "#000000",
      fillOpacity: fillOpacity ? fillOpacity : 0.35,
      clickable: false,
      draggable: false,
      editable: false,
      visible: true,
      radius: 30000,
      zIndex: 1,
    };
  };

  useEffect(() => {
    setLoadedLines(props.polylines);
  }, [props.polylines]);

  const getPolylines = () => {
    if (loadedLines) {
      return loadedLines.map((line, index) => {
        return (
          <Polyline
            key={`map-plyline-${index}`}
            path={line}
            options={getLineOptions(index)}
            onLoad={(p) => {
              setPolines([...polylines, p])
            }}
          />
        );
      });
    } else {
      return null;
    }
  };

  const showPolylines = useMemo(() => {
    return loadedLines && Array.isArray(loadedLines) && loadedLines.length > 0;
  }, [loadedLines]);

  return loadError ? (
    <CenterContainer>
      <PbErrorMessage message="Unable to load map" bigText />
    </CenterContainer>
  ) : isLoaded ? (
    <GoogleMap
      mapContainerStyle={props.containerStyle}
      center={props.center}
      zoom={props.zoom ? props.zoom : 15}
      onLoad={onLoad}
      onUnmount={onUnmount}
      onResize={() => {}}
      onZoomChanged={() => {
        if (gMap) {
          //if zoom callback defined pass zoom to parent
          if (props.onZoomChange) {
            props.onZoomChange(gMap.getZoom());
          }
        }
      }}
      options={{
        minZoom: 5,
        maxZoom: 19,
        mapTypeId: props.mapTypeId ? props.mapTypeId : "roadmap",
      }}
      ref={mapRef}
      onDragEnd={() => {
        if (props.onCenterChange && gMap) {
          const center = gMap.getCenter();
          const centarCoordinates = {
            lat: center.lat(),
            lng: center.lng(),
          };
          props.onCenterChange(centarCoordinates);
        }
      }}
    >
      {/* Child components, such as markers, info windows, etc. */}
      <>
        {showPolylines ? getPolylines() : null}
        {loadedMarkes.map((el, i) => {
          return (
            <OverlayView
              key={Math.random()}
              position={el.position}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              ref={(overlay) => (overlayViewRef.current[i] = overlay)}
            >
              <div
                onClick={() => {
                  if (gMap) {
                    moveCameraSmooth(el.position);
                  }
                }}
              >
                {props.component(el.compProps)}
              </div>
            </OverlayView>
          );
        })}
      </>
    </GoogleMap>
  ) : (
    <CenterContainer>
      <CircularProgress />
    </CenterContainer>
  );
}
